import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { AlertCircle, BarChart3 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getWhiteLabel } from "../../actions/cities";
import {
  type AnalyticsDashboard,
  getAnalyticsDashboardConfig,
} from "../../apis/analytics/analytics-apis";
import { useCity, useTheme } from "../../hooks";
import { isSuperAdmin } from "../../utils/utils";

const formatDate = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

const getDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  return {
    start_date: formatDate(startDate),
    end_date: formatDate(endDate),
  };
};

export const AnalyticsContainer = () => {
  const dispatch = useDispatch();
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWhitelabelId, setSelectedWhitelabelId] = useState<number | "">(
    ""
  );
  const { selectedCity } = useCity();

  const adminDetails = useSelector((state: any) => state.auth.adminDetails);
  const whitelabels = useSelector((state: any) => state.cities.whiteLabels);
  const isSuper = isSuperAdmin(adminDetails);
  const theme = useTheme();

  useEffect(() => {
    if (isSuper) {
      dispatch(getWhiteLabel() as any);
    }
  }, [dispatch, isSuper]);

  useEffect(() => {
    if (isSuper && !selectedWhitelabelId) {
      setDashboard(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchDashboardConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const whitelabelParam = isSuper
          ? (selectedWhitelabelId as number)
          : undefined;
        const config = await getAnalyticsDashboardConfig(whitelabelParam);

        if (config.dashboard) {
          setDashboard(config.dashboard);
        } else {
          setError("No analytics dashboard configured for this whitelabel");
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to load analytics config:", err);
        setError("Failed to load analytics dashboard");
        setLoading(false);
      }
    };

    fetchDashboardConfig();
  }, [isSuper, selectedWhitelabelId]);

  const { start_date, end_date } = useMemo(() => getDateRange(), []);

  const embedUrlWithParams = useMemo(() => {
    if (!dashboard?.embedUrl) return null;

    const params: string[] = [
      `start_date=${start_date}`,
      `end_date=${end_date}`,
    ];

    if (selectedCity) {
      params.push(`city=${selectedCity}`);
    }

    const url = `${dashboard.embedUrl}?${params.join("&")}#titled=false`;
    return url;
  }, [dashboard?.embedUrl, selectedCity, start_date, end_date]);

  const renderContent = () => {
    if (isSuper && !selectedWhitelabelId) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-flex-1">
          <BarChart3 className="tw-h-12 tw-w-12 tw-text-muted-foreground/50" />
          <div className="tw-text-center">
            <p className="tw-text-muted-foreground tw-font-medium">
              No Whitelabel Selected
            </p>
            <p className="tw-text-sm tw-text-muted-foreground/70">
              Select a whitelabel from the dropdown above to view analytics.
            </p>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-flex-1">
          <CircularProgress />
        </div>
      );
    }

    if (error || !dashboard) {
      // Determine if this is a configuration issue vs an actual error
      const isConfigError = error?.includes("not configured");
      const Icon = isConfigError ? BarChart3 : AlertCircle;
      const title = isConfigError
        ? "Analytics Not Configured"
        : "Unable to Load Analytics";
      const description = isConfigError
        ? "No analytics dashboard has been configured for this whitelabel."
        : error || "Dashboard not available. Please try again later.";

      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-flex-1">
          <Icon
            className={`tw-h-12 tw-w-12 ${isConfigError ? "tw-text-muted-foreground/50" : "tw-text-destructive/50"}`}
          />
          <div className="tw-text-center">
            <p
              className={`tw-font-medium ${isConfigError ? "tw-text-muted-foreground" : "tw-text-destructive"}`}
            >
              {title}
            </p>
            <p className="tw-text-sm tw-text-muted-foreground/70 tw-max-w-md">
              {description}
            </p>
          </div>
        </div>
      );
    }

    return (
      <iframe
        key={embedUrlWithParams || dashboard.embedUrl}
        src={embedUrlWithParams || dashboard.embedUrl}
        frameBorder="0"
        width="100%"
        height="100%"
        style={{ border: "none", flex: 1 }}
        title={dashboard.dashboardName}
        allowTransparency={true}
      />
    );
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          gap: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Analytics
        </Typography>
        <span
          style={{
            padding: "1px 6px",
            fontSize: "10px",
            fontWeight: 600,
            textTransform: "uppercase",
            backgroundColor: "transparent",
            border: `1.5px solid ${theme?.navBackground}`,
            borderRadius: "4px",
            letterSpacing: "0.025em",
            color: theme?.navBackground,
          }}
        >
          Beta
        </span>
        {isSuper && (
          <FormControl size="small" sx={{ minWidth: 200, marginLeft: "auto" }}>
            <InputLabel>Whitelabel</InputLabel>
            <Select
              value={selectedWhitelabelId}
              label="Whitelabel"
              onChange={(e) =>
                setSelectedWhitelabelId(e.target.value as number)
              }
            >
              {whitelabels?.map((wl: any) => (
                <MenuItem key={wl.id} value={wl.id}>
                  {wl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      {renderContent()}
    </Box>
  );
};
